import { useRef } from 'react';
import Icon from './Icon';
import Modal from './Modal';

// Styled replacement for window.confirm. The native dialog renders LTR with
// browser-chrome copy, which reads badly in an RTL Hebrew app and can't carry
// the record being acted on (supplier + item + amount) — so users confirm
// blind. This one uses the app's own .modal shell, so it inherits the RTL
// direction and the button language of every other dialog.
//
// `tone` picks the confirm button style: 'danger' for destructive flips,
// 'neutral' for a recorded judgement that is neither destructive nor a win,
// (cancelling a credit), 'success' for the affirmative ones, 'primary' for a
// plain "do" that is the dialog's main action (moving a draft, #304) — the
// navy .btn-primary, no icon.
// No `onConfirm` = a dialog that only informs: one button, labelled by
// `cancelLabel` (the move dialog when the draft can move nowhere).
// Escape cancels; clicking the backdrop cancels. There is no undo — the
// actions this guards are all reversible from the row menu.
//
// `children` render between the detail block and the buttons, for a confirm
// that has to COLLECT something before it can proceed — currently the credit
// close-reason chips. Paired with `confirmDisabled`, which is how the caller
// keeps the button unusable until the answer is there; a dialog that lets you
// press אישור and then fails with a server 400 teaches people to distrust it.
export default function ConfirmModal({
  title,
  message,
  // Consequence, then reassurance — as SEPARATE paragraphs. Running the two
  // together is what made the old copy read as a warning about data loss.
  // `message` stays for callers that only have one sentence.
  body = [],
  detail,
  children,
  // Name the ACTION, not the act of confirming: "מחק את הקו" is read before it
  // is clicked in a way "אישור" never is. That matters most right now, because
  // the confirming button has just moved to the other side of the dialog.
  confirmLabel = 'אישור',
  cancelLabel = 'ביטול',
  confirmDisabled = false,
  tone = 'success',
  onConfirm,
  onCancel,
}) {
  // Focus starts on CANCEL, always: the safe outcome should be the one a stray
  // Enter produces, and there is no confirm worth pre-arming. Cancel is also
  // first in the DOM, so Modal's own fallback would pick it for a plain confirm
  // anyway — passing the ref explicitly is what makes it hold when `children`
  // puts focusable content (the credit close-reason chips) above the footer.
  // Handed to <Modal> as initialFocus rather than focused after mount: a child
  // effect runs before the parent's, so focusing here would be overridden.
  const cancelRef = useRef(null);
  return (
    <Modal onClose={onCancel} title={title} size="sm" role="alertdialog"
      initialFocus={cancelRef}>
      {message && <div style={{ fontSize: '0.92rem', color: '#333', lineHeight: 1.5 }}>{message}</div>}
      {body.filter(Boolean).map((p, i) => (
        <p key={i} style={{ margin: (i || message) ? '10px 0 0' : 0, fontSize: '0.92rem', color: '#475569', lineHeight: 1.6 }}>
          {p}
        </p>
      ))}
      {detail && (
        <div style={{
          background: '#f7f9fc', borderRadius: 10, padding: '10px 14px',
          marginTop: 12, fontSize: '0.86rem', color: 'var(--ink-muted)',
        }}>{detail}</div>
      )}
      {children && <div style={{ marginTop: 14 }}>{children}</div>}
      <div className="modal-actions">
        <button ref={cancelRef} className="btn btn-secondary" onClick={onCancel}>{cancelLabel}</button>
        {/* Three tones, not two. 'neutral' exists for confirmations that are
            neither destructive nor an achievement — recording a judgement,
            like "these notes belong to next month". A green tick there
            congratulates the user for hiding money from a warning, and the
            tick specifically reads as "done, all good" on an action whose
            whole point is that nothing was resolved. */}
        {onConfirm && (
          <button
            className={`btn ${tone === 'danger' ? 'btn-danger' : tone === 'neutral' ? 'btn-outline' : tone === 'primary' ? 'btn-primary' : 'btn-success'}`}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {tone !== 'neutral' && tone !== 'primary' && <Icon name={tone === 'danger' ? 'close' : 'check'} size={16} />}
            {confirmLabel}
          </button>
        )}
      </div>
    </Modal>
  );
}
